import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/lazy-toaster";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { MobileCta } from "./components/MobileCta";
import { Cursor } from "./components/Cursor";
import { useLenis } from "./components/useLenis";
import { initAnalytics } from "./lib/analytics";
import { AttributionTracker } from "./components/AttributionTracker";
import "./App.css";

// Route-level code splitting: the shell (nav/footer) stays eager, each page is
// its own chunk fetched on demand — first-boot JS drops from one monolithic
// bundle to just the shell + the current page (TBT/unused-JS fix). Prerendered
// static HTML is unaffected: puppeteer waits for the lazy chunk at build time,
// so crawlers still receive complete pages.
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Careers = lazy(() => import("./pages/Careers"));
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Industries = lazy(() => import("./pages/Industries"));
const IndustryPage = lazy(() => import("./pages/IndustryPage"));
const OurWork = lazy(() => import("./pages/OurWork"));
const CaseStudy = lazy(() => import("./pages/CaseStudy"));
const Clients = lazy(() => import("./pages/Clients"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const Insights = lazy(() => import("./pages/Insights"));
const Article = lazy(() => import("./pages/Article"));
const RequestSurvey = lazy(() => import("./pages/RequestSurvey"));
const SurveyThanks = lazy(() => import("./pages/SurveyThanks"));
const Standards = lazy(() => import("./pages/Standards"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function Shell() {
  useLenis();
  useEffect(() => { initAnalytics(); }, []);
  return (
    <>
      <a href="#main-content" data-testid="skip-link"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:bg-dcc focus:px-4 focus:py-2 focus:text-white">
        Skip to content
      </a>
      <ScrollToTop />
      <AttributionTracker />
      <Cursor />
      <Navbar />
      <main id="main-content">
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/services" element={<Services />} />
          {/* paid-ads landing pages — duplicated service pages, noindex, canonical to the main page */}
          <Route path="/services/kitchen-exhaust-cleaning/lp2" element={<ServiceDetail slugOverride="kitchen-exhaust-cleaning" lpVariant="lp2" />} />
          <Route path="/services/robotic-air-duct-cleaning/lp2" element={<ServiceDetail slugOverride="robotic-air-duct-cleaning" lpVariant="lp2" />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/industries/:slug" element={<IndustryPage />} />
          <Route path="/our-work" element={<OurWork />} />
          <Route path="/our-work/:slug" element={<CaseStudy />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/locations/:slug" element={<LocationPage />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<Article />} />
          <Route path="/request-survey" element={<RequestSurvey />} />
          <Route path="/request-survey/thanks" element={<SurveyThanks />} />
          <Route path="/standards" element={<Standards />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
      <MobileCta />
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

export default App;
